import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data"],
  _excluded2 = ["data"];
import { registerMutation, registerQuery, useQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import { isLegacyKBDeleted } from 'knowledge-content-types/KnowledgeBaseV3Service/MigrationTypes';
import { useHasGate } from '../../auth/hooks/authHooks';
import { TEMPLATE_TYPE } from '../../constants/categoryManagement/HomepageCategorySettings';
import { knowledgeBaseMigrationReportSync, refetchCache } from '../../migration-handler/singleton/migrationHandler';
import { GATES } from '../../utils/gatesUtils';
import { getLanguageNameFromCode } from '../../utils/langUtils';
import KnowledgeBaseApi from '../api/KnowledgeBaseApi';
import { convertSCPToLegacyKnowledgeBase } from '../data/KnowledgeBase';
export const GET_AVAILABLE_LANGUAGES = registerQuery({
  fieldName: 'kbv3Languages',
  fetcher() {
    return http.get('cos-domains/v1/domain-setup-tasks/supported-languages').then(response => response.objects.map(({
      tag
    }) => ({
      value: tag,
      text: getLanguageNameFromCode(tag)
    })));
  }
});
export const KNOWLEDGE_BASES_QUERY = registerQuery({
  fieldName: 'knowledgeBases',
  fetcher() {
    return KnowledgeBaseApi.get();
  }
});
export const START_MIGRATION_MUTATION = registerMutation({
  fieldName: 'startMigration',
  args: ['knowledgeBaseId'],
  fetcher({
    knowledgeBaseId
  }) {
    return http.post('knowledge-content/v2/migration', {
      data: {
        legacyKnowledgeBaseId: knowledgeBaseId
      }
    });
  }
});
export const PUBLISH_MIGRATION_MUTATION = registerMutation({
  fieldName: 'publishMigratedKB',
  args: ['migrationId'],
  fetcher({
    migrationId
  }) {
    return http.post(`knowledge-content/v2/migration/publish/async/${migrationId}`);
  }
});
export const MIGRATION_REPORT_BY_ID_QUERY = registerQuery({
  fieldName: 'migrationReportById',
  args: ['migrationId'],
  fetcher({
    migrationId
  }) {
    return KnowledgeBaseApi.getMigrationReportById(migrationId);
  }
});
export const KNOWLEDGE_BASES_THEME_QUERY = registerQuery({
  fieldName: 'themeType',
  args: ['themeSettingsId'],
  fetcher({
    themeSettingsId: __themeSettingsId
  }) {
    return TEMPLATE_TYPE.CONTENT;
  }
});
export const GET_MIGRATION_ERROR_LINK_QUERY = registerQuery({
  fieldName: 'migrationErrorLink',
  args: ['migrationId'],
  fetcher({
    migrationId
  }) {
    return http.get(`knowledge-content/v2/migration/errors/file/${migrationId}`);
  }
});
export const useFetchKnowledgeBases = ({
  includeVariants = false
} = {
  includeVariants: false
}) => {
  const _useQuery = useQuery(KNOWLEDGE_BASES_QUERY),
    {
      data
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  const {
    hasGate
  } = useHasGate(GATES.KNOWLEDGE_BASE_V3_1);
  if (!hasGate) {
    return Object.assign({
      data: data ? data.knowledgeBases.objects : undefined
    }, rest);
  }
  const {
    new: newKBs,
    legacy,
    migrated
  } = knowledgeBaseMigrationReportSync();
  const migratedLegacyKbs = migrated.reduce((acc, {
    legacyKnowledgeBase,
    migration: {
      status
    }
  }) => {
    // Only show migrated legacy KBs if they haven't been deleted
    if (!isLegacyKBDeleted(status)) {
      acc.push(legacyKnowledgeBase);
    }
    return acc;
  }, []);
  return {
    loading: false,
    error: null,
    data: [...newKBs.map(({
      knowledgeBase
    }) => convertSCPToLegacyKnowledgeBase(knowledgeBase)), ...(includeVariants ? newKBs.reduce((acc, {
      knowledgeBase: {
        translations
      }
    }) => [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    ...acc, ...Object.values(translations || {}).map(convertSCPToLegacyKnowledgeBase)], []) : []), ...legacy.map(({
      knowledgeBase
    }) => knowledgeBase), ...migrated.map(({
      scpKnowledgeBase
    }) => convertSCPToLegacyKnowledgeBase(scpKnowledgeBase)), ...migratedLegacyKbs],
    refetch: refetchCache
  };
};
export const useFetchKnowledgeBase = knowledgeBaseId => {
  const _useFetchKnowledgeBas = useFetchKnowledgeBases(),
    {
      data
    } = _useFetchKnowledgeBas,
    rest = _objectWithoutPropertiesLoose(_useFetchKnowledgeBas, _excluded2);
  return Object.assign({
    data: data && knowledgeBaseId ? data.find(({
      id
    }) => id === knowledgeBaseId) : undefined
  }, rest);
};